import axios from '@/plugins/axios';

function generateFilterParams(filters) {
  const ret = {};
  const filterParameter = { ...filters };

  if (filterParameter.states != null && filterParameter.states.length > 0) {
    filterParameter.states = filterParameter.states.join(',');
  }

  if (filterParameter.lender != null && filterParameter.lender.length > 0) {
    filterParameter.lender = filterParameter.lender.join(',');
  }

  const ary = Object.keys(filterParameter).map((key) => ({
    name: key,
    value: filterParameter[key],
  }));

  ary.filter((i) => i.value != null && i.value !== '')
    .forEach((i) => { ret[`filters[${i.name}]`] = i.value; });

  return ret;
}

function normalize(str) {
  if (str == null) {
    return null;
  }

  const num = parseFloat(str);
  return isNaN(num) ? null : num;
}

const normalizeProduct = (product) => ({
  ...product,
  pricing: {
    ...product.pricing,
    initial: {
      ...product.pricing.initial,
      flatRatePrice: normalize(product.pricing?.initial.flatRatePrice),
      singleStatePrice: normalize(product.pricing?.initial.singleStatePrice),
      multiStatePrice: normalize(product.pricing?.initial.multiStatePrice),
      largeMultiStatePrice: normalize(product.pricing?.initial.largeMultiStatePrice),
    },
    resubmission: {
      ...product.pricing.resubmission,
      flatRatePrice: normalize(product.pricing?.resubmission.flatRatePrice),
      singleStatePrice: normalize(product.pricing?.resubmission.singleStatePrice),
      multiStatePrice: normalize(product.pricing?.resubmission.multiStatePrice),
      largeMultiStatePrice: normalize(product.pricing?.resubmission.largeMultiStatePrice),
    },
    sos: {
      ...product.pricing.sos,
      flatRatePrice: normalize(product.pricing?.sos.flatRatePrice),
      singleStatePrice: normalize(product.pricing?.sos.singleStatePrice),
      multiStatePrice: normalize(product.pricing?.sos.multiStatePrice),
      largeMultiStatePrice: normalize(product.pricing?.sos.largeMultiStatePrice),
    },
    mbr: {
      ...product.pricing.mbr,
      flatRatePrice: normalize(product.pricing?.mbr.flatRatePrice),
      singleStatePrice: normalize(product.pricing?.mbr.singleStatePrice),
      multiStatePrice: normalize(product.pricing?.mbr.multiStatePrice),
      largeMultiStatePrice: normalize(product.pricing?.mbr.largeMultiStatePrice),
    },
    lawChange: {
      ...product.pricing.lawChange,
      flatRatePrice: normalize(product.pricing?.lawChange.flatRatePrice),
      singleStatePrice: normalize(product.pricing?.lawChange.singleStatePrice),
      multiStatePrice: normalize(product.pricing?.lawChange.multiStatePrice),
      largeMultiStatePrice: normalize(product.pricing?.lawChange.largeMultiStatePrice),
    },
  },
});

const formatExpiresAfter = (hours) => {
  if (hours <= 24) {
    return `${hours} hours`;
  } else if (hours <= 8760) {
    const days = Math.round(hours / 24);

    return `${days} day${days !== 1 ? 's' : ''}`;
  }

  const years = Math.round(hours / 8766);

  return `${years} year${years !== 1 ? 's' : ''}`;
};

const state = {
  items: [],
  loading: false,
  pagination: {
    sortDesc: [true],
    page: 1,
    itemsPerPage: 10,
    sortBy: [],
  },
  filters: {},
  totalItems: 0,
  productTypes: [],
};

const mutations = {
  setProductTypes: (currentState, productTypes) => {
    currentState.productTypes = productTypes;
  },
  setItems: (currentState, { items, totalItems }) => {
    currentState.items = items;
    currentState.totalItems = totalItems;
  },
  setItem: (currentState, item) => {
    const index = currentState.items.findIndex((x) => x.id === item.id);
    currentState.items[index] = item;
  },
  setPagination: (currentState, payload) => {
    currentState.pagination = payload;
  },
  setFilters: (currentState, payload) => {
    currentState.filters = generateFilterParams(payload);
  },
  setLoading: (currentState, payload) => {
    currentState.loading = payload;
  },
};

const actions = ({
  getItems({ commit }) {
    return new Promise((resolve) => {
      commit('setLoading', true);
      const params = {
        page: state.pagination.page,
        rowsPerPage: state.pagination.itemsPerPage,
        sortBy: state.pagination.sortBy[0],
        descending: state.pagination.sortDesc[0],
        ...state.filters,
      };
      axios.get('/products', { params })
        .then((response) => {
          commit('setItems', response.data);
          resolve();
        }).finally(() => commit('setLoading', false));
    });
  },
  fetchProductTypes({ commit }) {
    return new Promise((resolve, reject) => {
      axios.get('/lists/product-types')
        .then((response) => {
          commit('setProductTypes', response.data);
          resolve();
        }).catch(reject);
    });
  },
  setPagination({ commit, dispatch }, paginationInfo) {
    commit('setPagination', paginationInfo);
    dispatch('getItems');
  },
  toggleActiveStatus({ commit, dispatch }, item) {
    return new Promise((resolve, reject) => {
      commit('setLoading', true);
      item.isActive = !item.isActive;
      axios.put(`/products/${item.id}/active`, item.isActive)
        .then(() => {
          resolve(dispatch('getItems'));
        })
        .catch(reject)
        .finally(() => dispatch('getItems'));
    });
  },
  getProduct(_, id) {
    return new Promise((resolve, reject) => {
      axios.get(`/products/${id}`).then((response) => {
        const product = response.data;
        product.expiresAfter = Number(product?.expiresAfter?.value?.totalHours ?? 0);
        resolve(product);
      }).catch(reject);
    });
  },
  updateProduct({ dispatch }, product) {
    return new Promise((resolve, reject) => {
      axios.put(`/products/${product.id}`, normalizeProduct(product))
        .then(() => {
          dispatch('getItems');
          resolve();
        }).catch(reject);
    });
  },
  createProduct({ dispatch }, product) {
    return new Promise((resolve, reject) => {
      axios.post('/products').then((response) => {
        product.id = response?.data?.id;
        resolve(dispatch('updateProduct', product));
      }).catch(reject);
    });
  },
  duplicateProduct({ dispatch }, productId) {
    return new Promise((resolve, reject) => {
      axios.post('/products', {}, { params: { 'copy-from': productId } })
        .then((response) => {
          if (response?.data) {
            dispatch('getItems').then(() => resolve(response.data)).catch(reject);
          } else {
            reject(new Error('Unable to duplicate product.'));
          }
        }).catch(reject);
    });
  },
});

const getters = {
  loading(currentState) {
    return currentState.loading;
  },
  pagination(currentState) {
    return currentState.pagination;
  },
  tableItems(currentState) {
    return [...currentState.items.map((product) => ({
      ...product,
      states: product.states.map((ste) => ste).join(', '),
      expiresAfter: formatExpiresAfter(product.expiresAfter),
      documents: product.documentationCollection
        .find((c) => c.submissionType === 'Initial')?.documents,
    })),
    ];
  },
  productsLookupUrl: () => (filters) => {
    const filterParams = generateFilterParams(filters);
    return `${axios.defaults.baseURL}products?${
      Object.keys(filterParams).map((key) => `${key}=${filterParams[key]}`).join('&')}`;
  },
  getSelectedProduct(currentState) {
    return currentState.selectedProduct;
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
